<div
  class="chat-audio-player rounded d-flex align-items-center gap-2 p-2 mt-1"
  [class.is-failed]="failed"
>
  <audio
    #audioRef
    preload="metadata"
    [src]="src"
    (loadedmetadata)="onLoadedMetadata()"
    (durationchange)="onDurationChange()"
    (timeupdate)="onTimeUpdate()"
    (ended)="onEnded()"
    (play)="onPlay()"
    (pause)="onPause()"
    (error)="onError()"
  ></audio>

  @if (failed) {
    <!-- The source could not be fetched or decoded (codec mismatch is a real case
         here — see the Safari/WebM caveat in the component). Recoverable in place
         rather than sending the user back to the file. -->
    <span
      class="chat-audio-player__toggle chat-audio-player__toggle--failed rounded-circle flex-shrink-0 d-inline-flex align-items-center justify-content-center"
    >
      <i class="ri-error-warning-line font-16" aria-hidden="true"></i>
    </span>
    <small class="flex-grow-1 chat-audio-player__error">{{
      "chat.audioPlayer.loadError" | translate
    }}</small>
    <button
      type="button"
      class="btn btn-sm chat-audio-player__action flex-shrink-0"
      (click)="retry()"
    >
      {{ "chat.bubble.retry" | translate }}
    </button>
  } @else {
    <button
      type="button"
      class="btn btn-sm chat-audio-player__toggle rounded-circle flex-shrink-0"
      [disabled]="awaitingMetadata"
      (click)="toggle()"
      [matTooltip]="
        (awaitingMetadata
          ? 'chat.audioPlayer.loading'
          : playing
            ? 'chat.audioPlayer.pause'
            : 'chat.audioPlayer.play'
        ) | translate
      "
      [attr.aria-label]="
        (awaitingMetadata
          ? 'chat.audioPlayer.loading'
          : playing
            ? 'chat.audioPlayer.pause'
            : 'chat.audioPlayer.play'
        ) | translate
      "
    >
      @if (awaitingMetadata) {
        <!-- Metadata in flight: without this the control looked ready and did
             nothing when pressed. -->
        <span
          class="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
      } @else {
        <i
          class="font-16"
          [ngClass]="playing ? 'ri-pause-fill' : 'ri-play-fill'"
          aria-hidden="true"
        ></i>
      }
    </button>

    <div class="flex-grow-1 overflow-hidden">
      <!-- Static pseudo-waveform — deterministic from messageId; click/←→ scrubs -->
      <div
        #waveform
        class="chat-audio-player__wave d-flex align-items-center gap-1"
        [class.is-loading]="awaitingMetadata"
        role="slider"
        [attr.tabindex]="awaitingMetadata ? -1 : 0"
        aria-valuemin="0"
        [attr.aria-valuemax]="ariaValueMax"
        [attr.aria-valuenow]="ariaValueNow"
        [attr.aria-disabled]="awaitingMetadata"
        [attr.aria-label]="'chat.audioPlayer.seek' | translate"
        (click)="onWaveClick($event)"
        (keydown)="onWaveKeydown($event)"
      >
        @for (bar of bars; let i = $index; track i) {
          <span
            class="chat-audio-player__bar flex-grow-1 rounded-pill"
            [class.chat-audio-player__bar--played]="i < playedBars"
            [style.height.%]="bar"
          ></span>
        }
      </div>
      <small class="d-block chat-audio-player__time">{{ timeLabel }}</small>
    </div>

    <button
      type="button"
      class="btn btn-sm chat-audio-player__action chat-audio-player__rate flex-shrink-0"
      (click)="cycleRate()"
      [matTooltip]="'chat.audioPlayer.speed' | translate"
      [attr.aria-label]="'chat.audioPlayer.speed' | translate"
    >
      {{ rateLabel }}
    </button>

    <a
      class="btn btn-sm chat-audio-player__action flex-shrink-0"
      [href]="src"
      target="_blank"
      rel="noopener noreferrer"
      download
      [attr.download]="fileName || null"
      [matTooltip]="'chat.audioPlayer.download' | translate"
      [attr.aria-label]="'chat.audioPlayer.download' | translate"
    >
      <i class="ri-download-2-line" aria-hidden="true"></i>
    </a>
  }
</div>
